import type { OverlayManager } from "./OverlayManager";
import { OverlayType, type RulerConfig, type TextOverlay } from "./types";
import { closestHigherStep, formatRulerLabel } from "./rulerScale";

/**
 * Ruler drawing: graduations and position handles, as overlays. Stateless:
 * RulerController owns the positions and keeps the returned overlay ids.
 */

type Point = { x: number; y: number };

/** World ↔ screen conversions of the viewer. */
export interface RulerProjection {
  screenToWorld(screen: Point): Point;
  worldToScreen(world: Point): Point;
}

const MAX_GRADUATIONS = 500; // Safety limit

/**
 * Graduations and labels of one ruler, from its bar position (top of the
 * horizontal bar, left of the vertical one).
 */
export function paintGraduations(
  overlays: OverlayManager,
  config: RulerConfig,
  view: RulerProjection,
  canvas: { width: number; height: number },
  ruler: {
    orientation: "horizontal" | "vertical";
    /** Horizontal: bar top (y). Vertical: bar left (x). */
    position: number;
    origin: Point;
    spacing: number;
  },
): string[] {
  const { orientation, position, spacing } = ruler;
  const horizontal = orientation === "horizontal";
  const ids: string[] = [];

  // Each ruler spans its full length independently
  const startWorld = view.screenToWorld(
    horizontal ? { x: 0, y: position } : { x: position, y: canvas.height },
  );
  const endWorld = view.screenToWorld(
    horizontal ? { x: canvas.width, y: position } : { x: position, y: 0 },
  );

  const dirIndex = horizontal ? "x" : "y";
  const origin = ruler.origin[dirIndex];
  const minVal = Math.min(startWorld[dirIndex], endWorld[dirIndex]);
  const maxVal = Math.max(startWorld[dirIndex], endWorld[dirIndex]);

  // First graduation position
  let pos = closestHigherStep(origin, minVal, spacing);
  let count = 0;

  while (pos <= maxVal && count < MAX_GRADUATIONS) {
    const stepFromOrigin = Math.round((pos - origin) / spacing);
    const isMainGrad = stepFromOrigin % config.subGraduationCount === 0;
    const gradLength = isMainGrad
      ? config.graduationLength
      : config.subGraduationLength;

    const screenPos = view.worldToScreen(
      horizontal ? { x: pos, y: startWorld.y } : { x: startWorld.x, y: pos },
    );
    const along = horizontal ? screenPos.x : screenPos.y;

    ids.push(
      horizontal
        ? overlays.createLine(along, position, along, position + gradLength, {
            strokeColor: config.lineColor,
            strokeWidth: 1,
          })
        : overlays.createLine(position, along, position + gradLength, along, {
            strokeColor: config.lineColor,
            strokeWidth: 1,
          }),
    );

    // Label for main graduations (on the handle side, away from the border)
    if (isMainGrad) {
      const edge = position + config.thickness - 2;
      ids.push(
        overlays.add({
          type: OverlayType.Text,
          x: horizontal ? along - 2 : edge,
          y: horizontal ? edge : along - 2,
          text: formatRulerLabel(pos - origin, spacing),
          fontSize: config.labelFontSize,
          textAlign: "right" as CanvasTextAlign,
          textBaseline: "bottom" as CanvasTextBaseline,
          style: { fillColor: config.textColor },
        } as Omit<TextOverlay, "id">),
      );
    }

    pos += spacing;
    count++;
  }

  return ids;
}

const ARROW_SIZE = 4;

/**
 * Position handle of a ruler: a disc with two arrows showing the direction
 * it moves in (up/down for the horizontal ruler, left/right for the vertical
 * one).
 */
export function paintRulerHandle(
  overlays: OverlayManager,
  config: RulerConfig,
  center: Point,
  orientation: "horizontal" | "vertical",
): string[] {
  const { x, y } = center;
  const stroke = { strokeColor: config.handleBorderColor, strokeWidth: 2 };
  const ids = [
    overlays.createPoint(x, y, config.handleSize / 2, {
      fillColor: config.originColor,
      strokeColor: config.handleBorderColor,
    }),
  ];

  // Each arrow is a chevron: two strokes from its tip.
  for (const side of [-1, 1]) {
    const tip =
      orientation === "horizontal"
        ? { x, y: y + side * ARROW_SIZE }
        : { x: x + side * ARROW_SIZE, y };
    for (const spread of [-1, 1]) {
      const end =
        orientation === "horizontal"
          ? { x: x + spread * ARROW_SIZE, y: y + side }
          : { x: x + side, y: y + spread * ARROW_SIZE };
      ids.push(overlays.createLine(tip.x, tip.y, end.x, end.y, stroke));
    }
  }

  return ids;
}
